import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAuth } from '@/hooks/useAuth';
import type { WebPublishView } from './webPublishCopy';

const QUERY_KEY = ['admin', 'store-web-publish'];

function asView(raw: Partial<WebPublishView> | undefined): WebPublishView {
    return {
        pendingSince: raw?.pendingSince ?? null,
        forceNow: raw?.forceNow === true,
        lastState: raw?.lastState || 'idle',
        lastFinishedAt: raw?.lastFinishedAt ?? null,
        lastError: raw?.lastError || '',
    };
}

export function useStoreWebPublish() {
    const { user } = useAuth();
    const qc = useQueryClient();
    const query = useQuery({
        queryKey: QUERY_KEY,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_store_web_publish', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            return asView(normalizeRpcResponse(data)[0] as Partial<WebPublishView> | undefined);
        },
        refetchInterval: (query) => {
            const state = query.state.data?.lastState;
            if (state === 'queued' || state === 'running' || query.state.data?.pendingSince) return 15000;
            return false;
        },
    });

    const request = useMutation({
        mutationFn: async () => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { error } = await dbClient.rpc('admin_request_store_web_publish', {
                p_admin_user_id: user.id,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });

    return { query, request };
}
